import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';


const INITIAL_PEDIATRIC_ABDOMEN_DATA = {
  examType: 'Pediatric Abdomen',
  examDate: new Date().toISOString().split('T')[0],
  firstName: '',
  lastName: '',
  mrn: '',
  technologist: '',
  reason: '',
  callReport: false,
  phoneNumber: '',
  history: '',
  comments: '',
  appendix: {
    evaluated: false,
    status: '',
    notCompressible: false,
    periappendicealFluid: false,
    appendicolith: false,
    diameter: ''
  },
  pylorus: {
    evaluated: false,
    status: '',
    wallThickness: '',
    pylorusLength: '',
    peristalsis: false
  },
  intussusception: {
    evaluated: false,
    status: '',
    dilatedBowel: false,
    freeFluid: false
  }
};

function PediatricAbdomenForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Appendix, 1=Pylorus, 2=Intussusception
  const [activeTab, setActiveTab] = useState(0);
  const [showWallThicknessTooltip, setShowWallThicknessTooltip] = useState(false);
  const [showLengthTooltip, setShowLengthTooltip] = useState(false);
  // Fetch dynamic technologists list
  const { technologists, defaultTech } = useTechnologists();
  
  const {
    formData,
    updateField,
    updateNestedField,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    resetForm,
    isLoading,
    error,
    success,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    {
      ...INITIAL_PEDIATRIC_ABDOMEN_DATA,
      technologist: defaultTech 
     },
      'Pediatric Abdomen', 
    {
    token, facilityInfo
    }
  );

  // Update technologist field when defaultTech loads
  useEffect(() => {
    if (defaultTech && !formData.technologist) {
      updateField('technologist', defaultTech);
    }
  }, [defaultTech, formData.technologist, updateField]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Appendix
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Pylorus
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(2)}
              className={`worksheet-tab-btn${activeTab === 2 ? ' active' : ''}`}
            >
              Intussusception
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Appendix Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.appendix.evaluated}
                      onChange={(e) => updateNestedField('appendix', 'evaluated', e.target.checked)}
                    />
                    {' '}Appendix Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.appendix.evaluated ? 1 : 0.4, pointerEvents: formData.appendix.evaluated ? 'auto' : 'none' }}>
                  <h3 style={{ color: 'var(--color-primary)' }}>Appendix</h3>
            
            <div className="form-group">
              <label>Status:</label>
              <select
                value={formData.appendix.status}
                onChange={(e) => updateNestedField('appendix', 'status', e.target.value)}
                disabled={!formData.appendix.evaluated}
              >
                <option value="">Select status</option>
                <option value="Normal">Normal</option>
                <option value="Abnormal">Abnormal</option>
                <option value="Not Visualized">Not Visualized</option>
              </select>
            </div>

            <div className="form-group">
              <label>Appendix Diameter (mm):</label>
              <input
                type="number"
                step="0.1"
                value={formData.appendix.diameter}
                onChange={(e) => updateNestedField('appendix', 'diameter', e.target.value)}
                placeholder="Enter diameter"
                disabled={!formData.appendix.evaluated}
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.appendix.notCompressible}
                  onChange={(e) => updateNestedField('appendix', 'notCompressible', e.target.checked)}
                  disabled={!formData.appendix.evaluated}
                />
                {' '}Not Compressible
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.appendix.periappendicealFluid}
                  onChange={(e) => updateNestedField('appendix', 'periappendicealFluid', e.target.checked)}
                  disabled={!formData.appendix.evaluated}
                />
                {' '}Periappendiceal Fluid
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.appendix.appendicolith}
                  onChange={(e) => updateNestedField('appendix', 'appendicolith', e.target.checked)}
                  disabled={!formData.appendix.evaluated}
                />
                {' '}Appendicolith
              </label>
            </div>
                </div>
              </div>
            )}

            {/* Pylorus Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.pylorus.evaluated}
                      onChange={(e) => updateNestedField('pylorus', 'evaluated', e.target.checked)}
                    />
                    {' '}Pylorus Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.pylorus.evaluated ? 1 : 0.4, pointerEvents: formData.pylorus.evaluated ? 'auto' : 'none' }}>
                  <h3 style={{ color: 'var(--color-primary)' }}>Pylorus</h3>
            
            <div className="form-group">
              <label>Status:</label>
              <select
                value={formData.pylorus.status}
                onChange={(e) => updateNestedField('pylorus', 'status', e.target.value)}
                disabled={!formData.pylorus.evaluated}
              >
                <option value="">Select status</option>
                <option value="Normal">Normal</option>
                <option value="Abnormal"> Abnormal</option>
                <option value="Not Visualized">Not Visualized</option>
              </select>
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>
                Wall Thickness (mm):
                <span 
                  style={{ 
                    marginLeft: '8px', 
                    cursor: 'help',
                    color: 'var(--color-primary)',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                  onMouseEnter={() => setShowWallThicknessTooltip(true)}
                  onMouseLeave={() => setShowWallThicknessTooltip(false)}
                >
                  ℹ️
                </span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.pylorus.wallThickness}
                onChange={(e) => updateNestedField('pylorus', 'wallThickness', e.target.value)}
                placeholder="Enter wall thickness"
                disabled={!formData.pylorus.evaluated || formData.pylorus.status === 'Not Visualized'}
              />
              {showWallThicknessTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  zIndex: 1000,
                  backgroundColor: 'var(--color-card)',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '8px',
                  padding: '10px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  marginTop: '5px'
                }}>
                  <img 
                    src="/images/pylorus.png" 
                    alt="Pylorus measurement guide" 
                    style={{ 
                      maxWidth: '400px', 
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </div>
              )}
            </div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label>
                Pylorus Length (mm):
                <span 
                  style={{ 
                    marginLeft: '8px', 
                    cursor: 'help',
                    color: 'var(--color-primary)',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                  onMouseEnter={() => setShowLengthTooltip(true)}
                  onMouseLeave={() => setShowLengthTooltip(false)}
                >
                  ℹ️
                </span>
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.pylorus.pylorusLength}
                onChange={(e) => updateNestedField('pylorus', 'pylorusLength', e.target.value)}
                placeholder="Enter pylorus length"
                disabled={!formData.pylorus.evaluated || formData.pylorus.status === 'Not Visualized'}
              />
              {showLengthTooltip && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0',
                  zIndex: 1000,
                  backgroundColor: 'var(--color-card)',
                  border: '2px solid var(--color-primary)',
                  borderRadius: '8px',
                  padding: '10px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  marginTop: '5px'
                }}>
                  <img 
                    src="/images/pylorus.png" 
                    alt="Pylorus measurement guide" 
                    style={{ 
                      maxWidth: '400px', 
                      height: 'auto',
                      display: 'block'
                    }} 
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.pylorus.peristalsis}
                  onChange={(e) => updateNestedField('pylorus', 'peristalsis', e.target.checked)}
                  disabled={!formData.pylorus.evaluated}
                />
                {' '}No peristalsis
              </label>
            </div>
                </div>
              </div>
            )}

            {/* Intussusception Tab */}
            {activeTab === 2 && (
              <div className="tab-content-grid">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.intussusception.evaluated}
                      onChange={(e) => updateNestedField('intussusception', 'evaluated', e.target.checked)}
                    />
                    {' '}Intussusception Evaluated
                  </label>
                </div>

                <div style={{ opacity: formData.intussusception.evaluated ? 1 : 0.4, pointerEvents: formData.intussusception.evaluated ? 'auto' : 'none' }}>
                  <h3 style={{ color: 'var(--color-primary)' }}>Intussusception</h3>
            
            <div className="form-group">
              <label>Status:</label>
              <select
                value={formData.intussusception.status}
                onChange={(e) => updateNestedField('intussusception', 'status', e.target.value)}
                disabled={!formData.intussusception.evaluated}
              >
                <option value="">Select status</option>
                <option value="No intussusception visualized">No intussusception visualized</option>
                <option value="Intussusception visualized">Intussusception visualized</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.intussusception.dilatedBowel}
                  onChange={(e) => updateNestedField('intussusception', 'dilatedBowel', e.target.checked)}
                  disabled={!formData.intussusception.evaluated}
                />
                {' '}Dilated Bowel
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.intussusception.freeFluid}
                  onChange={(e) => updateNestedField('intussusception', 'freeFluid', e.target.checked)}
                  disabled={!formData.intussusception.evaluated}
                />
                {' '}Free Fluid
              </label>
            </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default PediatricAbdomenForm;
